import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/apiClient";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { SlidersHorizontal, Mountain, Loader2, Heart } from "lucide-react";
import { BulkBar, RowActions, RowCheck, useBulkSelect } from "@/components/AdminBulkActions";
import { RequestDetailDialog } from "@/components/RequestDetailDialog";

export default function RequestsRegistry() {
  const [items, setItems] = useState([]);
  const [converting, setConverting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const sel = useBulkSelect();
  const navigate = useNavigate();
  const load = () => api.get(`/admin/requests${showArchived ? "?include_archived=true" : ""}`).then(({ data }) => { setItems(data); sel.clear(); });
  useEffect(() => { load(); }, [showArchived]); // eslint-disable-line

  const trasforma = async (rid) => {
    setConverting(rid);
    try {
      const { data } = await api.post(`/catnat/requests/${rid}/trasforma`);
      toast.success("Lead trasformato in preventivo dell'Agenzia");
      navigate(`/admin/preventivi?open=${data.quote_id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setConverting(null); }
  };

  const trasformaPf = async (rid) => {
    setConverting(rid);
    try {
      const { data } = await api.post(`/protezione-famiglia/requests/${rid}/trasforma`);
      toast.success("Lead trasformato in preventivo dell'Agenzia");
      navigate(`/admin/preventivi?open=${data.quote_id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setConverting(null); }
  };

  const trasformaRete = async (rid) => {
    setConverting(rid);
    try {
      const { data } = await api.post(`/rete-solidale/requests/${rid}/trasforma`);
      toast.success("Lead trasformato in preventivo Infortuni (Formula Service)");
      setDetail(null);
      navigate(`/admin/preventivi/${data.quote_id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setConverting(null); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Registro richieste</h1>
        <p className="mt-1 text-muted-foreground">Lead aggregati da tutti i partner (link partner e pubblico), incluse le landing pubbliche <span className="font-medium text-forest">CAT NAT</span>, <span className="font-medium text-forest">Protezione Famiglia</span> e <span className="font-medium text-forest">Rete Solidale</span>. Clicca sul <span className="font-medium text-forest">nominativo</span> per vedere tutti i dati inseriti dal cliente. Usa "Trasforma in preventivo" per intestarli all'Agenzia.</p>
      </div>
      <Card className="overflow-hidden rounded-xl border-border/60">
        <div className="p-3">
          <BulkBar endpoint="/admin/requests/bulk" selected={sel.selected} onDone={load}
            showArchived={showArchived} onToggleArchived={() => setShowArchived((s) => !s)} entityLabel="richieste" />
        </div>
        <Table>
          <TableHeader><TableRow className="bg-sage/50">
            <TableHead className="w-10"><Checkbox checked={items.length > 0 && sel.count >= items.length} onCheckedChange={() => sel.toggleAll(items.map((x) => x.id))} data-testid="select-all" /></TableHead>
            <TableHead>Ente</TableHead><TableHead>Cluster</TableHead><TableHead>Partner</TableHead>
            <TableHead>Canale</TableHead><TableHead>Contatti</TableHead><TableHead>Stato</TableHead><TableHead>Proposta</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">Nessuna richiesta.</TableCell></TableRow>}
            {items.map((r) => (
              <TableRow key={r.id} data-testid={`admin-request-${r.id}`}>
                <TableCell><RowCheck id={r.id} selected={sel.isSelected(r.id)} onToggle={sel.toggle} /></TableCell>
                <TableCell className="font-medium">
                  <button
                    onClick={() => setDetail(r)}
                    data-testid={`request-detail-${r.id}`}
                    className="text-left text-forest underline-offset-2 hover:text-terracotta hover:underline"
                  >
                    {r.dati_cliente?.ragione_sociale || `${r.dati_cliente?.nome || ""} ${r.dati_cliente?.cognome || ""}`.trim() || "—"}
                  </button>
                </TableCell>
                <TableCell className="text-sm">{r.cluster || "—"}</TableCell>
                <TableCell className="text-sm">{r.codice_collab || (r.canale === "landing_pubblica" ? "Agenzia" : "—")}</TableCell>
                <TableCell><Badge variant={r.canale === "partner" ? "outline" : "secondary"}>{r.canale}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.dati_cliente?.email || r.dati_cliente?.telefono || "—"}</TableCell>
                <TableCell><StatusBadge state={r.stato} /></TableCell>
                <TableCell>
                  {r.quote_id ? (
                    <Button size="sm" variant="ghost" data-testid={`request-open-quote-${r.id}`}
                      onClick={() => navigate(`/admin/preventivi?open=${r.quote_id}`)}
                      className="h-7 gap-1 text-forest hover:text-terracotta">
                      <SlidersHorizontal className="h-3.5 w-3.5" /> Apri proposta
                    </Button>
                  ) : r.product_code === "cat_nat_impresa" ? (
                    <Button size="sm" variant="outline" data-testid={`request-trasforma-${r.id}`} disabled={converting === r.id}
                      onClick={() => trasforma(r.id)} className="h-7 gap-1 border-forest/40 text-forest hover:bg-sage">
                      {converting === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mountain className="h-3.5 w-3.5" />} Trasforma in preventivo
                    </Button>
                  ) : r.campagna === "protezione_famiglia" ? (
                    <Button size="sm" variant="outline" data-testid={`request-trasforma-pf-${r.id}`} disabled={converting === r.id}
                      onClick={() => trasformaPf(r.id)} className="h-7 gap-1 border-forest/40 text-forest hover:bg-sage">
                      {converting === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Heart className="h-3.5 w-3.5" />} Trasforma in preventivo
                    </Button>
                  ) : r.canale === "rete_solidale" ? (
                    <Button size="sm" variant="outline" data-testid={`request-trasforma-rete-${r.id}`} disabled={converting === r.id}
                      onClick={() => trasformaRete(r.id)} className="h-7 gap-1 border-terracotta/40 text-terracotta hover:bg-[#FFF7EC]">
                      {converting === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Heart className="h-3.5 w-3.5" />} Trasforma in preventivo
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                  <RowActions endpoint="/admin/requests/bulk" id={r.id} archived={showArchived} onDone={load} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <RequestDetailDialog request={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)}
        onTrasforma={trasformaRete} converting={converting} />
    </div>
  );
}
